import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isBusiness: state.components.contactUsForm.fieldValues["isBusiness"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface IsBusinessStyles {
    radioGroup?: RadioGroupProps;
}

export const isBusinessStyles: IsBusinessStyles = {
    radioGroup: {
        css: css`
            margin: 1em 0;
        `,
        labelProps: {
            weight: "bold",
            size: 15,
            css: css`
                font-size: 15px;
            `,
        },
    },
};

const styles = isBusinessStyles;

const IsBusiness: React.FC<Props> = ({ fields, isBusiness, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [isBusinessError, setIsBusinessError] = React.useState(false);

    React.useEffect(() => {
        contactFormContext.validators.isBusiness = validateIsBusiness;
        if (typeof isBusiness !== "undefined") {
            validateIsBusiness();
        }
    }, [isBusiness]);

    React.useEffect(() => {
        contactFormContext.validators.isBusiness = validateIsBusiness;
    }, [isRequired]);

    const validateIsBusiness = () => {
        const errorMessage = !!(isRequired && isBusiness !== "true");
        setIsBusinessError(errorMessage);
        return !errorMessage;
    };

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        setFieldValue({ key: "isBusiness", value: event.target.value === "true" ? "true" : "false" });
    };

    return (
        <>
            <RadioGroup
                {...styles.radioGroup}
                value={isBusiness}
                onChangeHandler={addressTypeChangeHandler}
                label={label}
            >
                <Radio value="false">{translate("Personal")}</Radio>
                <Radio value="true">{translate("Business")}</Radio>
            </RadioGroup>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(IsBusiness),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "This is a business",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
